/* eslint-disable @typescript-eslint/no-explicit-any */
import { Component, Input, OnDestroy, OnInit, inject } from '@angular/core';
import { UntypedFormArray, UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { NgSelectComponent } from '@ng-select/ng-select';
import { IBoton, IListado, IQueryParam } from 'modelos/src';
import { Subscription, firstValueFrom } from 'rxjs';
import { HelperService } from '../../../auxiliares/servicios/helper.service';
import { ListadosService } from '../../../auxiliares/servicios/listados.service';
import { carpetaCategoria, ClientesService } from '../clientes.service';
import {
  BOTONERA_OPCIONES,
  BOTONERA_SPECS,
  BotoneraTemplateId,
  textoHint,
} from '../../../auxiliares/plantillas/plantillas-spec';
import { crearGrupoBoton } from '../provisionador/boton-form/boton-form.component';
import {
  construirPreviewVM,
  PreviewVM,
} from '../provisionador/preview/preview-vm';
import { PreviewModo } from '../provisionador/preview/provisionador-preview.component';

/** Slot del editor → campo de imagen de `ICategoriaCliente` (override por categoría). */
export const SLOT_A_CATEGORIA_CLIENTE: Record<string, string> = {
  arriba: 'imagenArriba',
  abajo: 'imagenAbajo',
  fondo: 'imagenFondo',
  sirena: 'imagenSirena',
  reflector: 'imagenReflector',
  adelante: 'imagenAdelante',
  atras: 'imagenAtras',
};

interface SlotImagen {
  control: string;
  label: string;
  hint: string;
  scale: string;
}

/**
 * Editor de UNA categoría, COMPARTIDO por el provisionador y el editor de cliente. Opera sobre las
 * estructuras que recibe POR REFERENCIA (form group + botones FormArray + ids arrays + imágenes),
 * igual que `categoria-armado`. Incluye: chooser de botonera, alta/baja de botones (boton-form),
 * agregado de botones existentes por referencia (solo lectura; se editan en el CRUD de Botones),
 * armado (reorden), imágenes override y preview en vivo de la categoría.
 */
@Component({
  selector: 'app-categoria-editor',
  templateUrl: './categoria-editor.component.html',
  styleUrls: ['./categoria-editor.component.scss'],
  standalone: false,
})
export class CategoriaEditorComponent implements OnInit, OnDestroy {
  private fb = inject(UntypedFormBuilder);
  private clientesService = inject(ClientesService);
  private listadosService = inject(ListadosService);
  private helper = inject(HelperService);

  /** Categoría (nombre/variante/prioridad/color/inicioTemplate/nombreTemplate). */
  @Input() grupo!: UntypedFormGroup;
  /** Botones de la categoría (FormArray de grupos creados con `crearGrupoBoton`). */
  @Input() botones!: UntypedFormArray;
  @Input() idsPrincipales: string[] = [];
  @Input() idsSecundarios: string[] = [];
  @Input() idsOtros: string[] = [];
  /** Overrides de imagen por slot (arriba/abajo/fondo/sirena/reflector/adelante/atras). */
  @Input() imagenes!: UntypedFormGroup;
  /** Nombre del cliente (ruta de subida de imágenes). */
  @Input() nombreCliente = '';
  /**
   * Identidad de la categoría dentro del cliente. Va en la ruta de subida junto al nombre: sin
   * esto, dos categorías con el mismo `nombre` (y distinta `variante`) se pisan las imágenes.
   * En el provisionador la categoría todavía no existe, así que alcanza con algo único en el alta.
   */
  @Input() idCategoria = '';
  /** Valor del form del cliente (para el preview: loginTemplate, imagenes, configuracion, colorTema). */
  @Input() clienteFormValue: any = {};
  @Input() sirenas = false;
  /** Mostrar el preview interno por categoría. false en el provisionador (tiene preview global). */
  @Input() mostrarPreview = true;

  public loading = false;
  public previewModo: PreviewModo = 'botonera';
  public botoneraOpciones = BOTONERA_OPCIONES;
  private contadorTmp = 0;

  /** Catálogo completo de botones para el selector "usar existente". */
  public botonesExistentes: IBoton[] = [];
  private botonesExistentes$?: Subscription;

  get botonesControls(): UntypedFormGroup[] {
    return (this.botones?.controls ?? []) as UntypedFormGroup[];
  }
  /** Familia de la categoría; si no, la del cliente. */
  get botoneraId(): BotoneraTemplateId | undefined {
    return (
      (this.grupo?.get('inicioTemplate')?.value as BotoneraTemplateId) ||
      (this.clienteFormValue?.inicioTemplate as BotoneraTemplateId)
    );
  }
  get cantidadPrincipales(): number {
    return this.idsPrincipales.length || 1;
  }
  get nombreValido(): boolean {
    return !!this.nombreCliente;
  }

  /** Slots de imagen override, derivados de la botonera de la categoría. */
  get slotsCategoria(): SlotImagen[] {
    const fam = this.botoneraId;
    if (!fam || !BOTONERA_SPECS[fam]) return [];
    const out: SlotImagen[] = [];
    const vistos = new Set<string>();
    for (const s of BOTONERA_SPECS[fam].slots) {
      if (s.slot.startsWith('urlImagen')) continue;
      if (!(s.slot in SLOT_A_CATEGORIA_CLIENTE)) continue;
      if ((s.slot === 'reflector' || s.slot === 'sirena') && !this.sirenas) continue;
      if (vistos.has(s.slot)) continue;
      vistos.add(s.slot);
      out.push({ control: s.slot, label: s.label, hint: textoHint(s), scale: s.scale });
    }
    return out;
  }

  /** Preview de ESTA categoría: base del cliente + su botonera + imágenes override. */
  get previewVm(): PreviewVM {
    const base = this.clienteFormValue ?? {};
    const catImgs = this.imagenes?.value ?? {};
    const overrides: Record<string, string> = {};
    for (const [slot, val] of Object.entries(catImgs)) {
      if (val) overrides[slot] = val as string;
    }
    const formValue = {
      ...base,
      inicioTemplate: this.botoneraId,
      sirenas: this.sirenas,
      imagenes: { ...(base.imagenes ?? {}), ...overrides },
    };
    return construirPreviewVM(
      formValue,
      this.botones?.value ?? [],
      this.idsPrincipales,
      this.idsOtros,
      this.idsSecundarios,
    );
  }

  /** `null` = sin plantilla propia: la categoria hereda la del cliente (o la custom por bundleId). */
  public setBotonera(id: string | null): void {
    this.grupo?.patchValue({ inicioTemplate: id });
    this.previewModo = 'botonera';
  }

  public agregarBoton(): void {
    const tmpId = `tmp-${++this.contadorTmp}`;
    this.botones.push(crearGrupoBoton(this.fb, tmpId));
    this.idsPrincipales.push(tmpId);
  }

  /**
   * Convierte un botón nuevo (en blanco) en una REFERENCIA a uno ya creado: reemplaza el
   * FormGroup por uno con _tmpId = _id real (no se crea ni edita al guardar) manteniendo
   * su posición en las listas del armado.
   */
  public usarExistente(
    index: number,
    boton: IBoton | undefined,
    select: NgSelectComponent,
  ): void {
    if (!boton?._id) return;
    const yaUsado = this.botonesControls.some(
      (g) => String(g.get('_tmpId')?.value) === boton._id,
    );
    if (yaUsado) {
      this.helper.notifWarn('Ese botón ya está en la categoría');
      select.clearModel();
      return;
    }
    const tmpId = String(this.botonesControls[index]?.get('_tmpId')?.value);
    this.botones.setControl(index, crearGrupoBoton(this.fb, boton._id, boton));
    const reemplazar = (arr: string[]) => {
      const i = arr.indexOf(tmpId);
      if (i >= 0) arr[i] = boton._id!;
    };
    reemplazar(this.idsPrincipales);
    reemplazar(this.idsSecundarios);
    reemplazar(this.idsOtros);
  }

  /** Botón existente (referenciado): se muestra solo lectura; se edita en el CRUD de Botones. */
  public esExistente(g: UntypedFormGroup): boolean {
    return !String(g.get('_tmpId')?.value).startsWith('tmp-');
  }

  public searchBoton = (term: string, item: IBoton): boolean => {
    if (item.nombre?.toLowerCase().includes(term.toLowerCase())) return true;
    if (item.variante?.toLowerCase().includes(term.toLowerCase())) return true;
    return false;
  };

  public eliminarBoton(index: number): void {
    const grupo = this.botonesControls[index];
    const tmpId = grupo?.get('_tmpId')?.value;
    this.botones.removeAt(index);
    const quitar = (arr: string[]) => {
      const i = arr.indexOf(tmpId);
      if (i >= 0) arr.splice(i, 1);
    };
    quitar(this.idsPrincipales);
    quitar(this.idsSecundarios);
    quitar(this.idsOtros);
  }

  async ngOnInit(): Promise<void> {
    await this.listarBotones();
  }

  ngOnDestroy(): void {
    this.botonesExistentes$?.unsubscribe();
  }

  private async listarBotones(): Promise<void> {
    const query: IQueryParam = { sort: 'nombre', limit: 0 };
    this.botonesExistentes$?.unsubscribe();
    this.botonesExistentes$ = this.listadosService
      .subscribe<IListado<IBoton>>('botones', query)
      .subscribe((data) => {
        this.botonesExistentes = data.datos;
        console.debug(new Date().toLocaleString(), `listado de botones`, data);
      });
    await this.listadosService.getLastValue('botones', query);
  }

  /** Sube una imagen override de la categoría (clave única por categoría). */
  public async setFileCategoria(event: Event, control: string): Promise<void> {
    if (!this.nombreValido) {
      this.helper.notifWarn('Primero poné el nombre del cliente');
      return;
    }
    const file = (event as any).target?.files?.item(0);
    if (!file) return;
    this.loading = true;
    try {
      const res = await firstValueFrom(
        this.clientesService.subirImagen(
          file,
          this.nombreCliente,
          control,
          carpetaCategoria(this.grupo?.get('nombre')?.value, this.idCategoria),
        ),
      );
      const val = res.url + `?${(Math.random() * 1000).toFixed(0)}`;
      this.imagenes.patchValue({ [control]: val });
      // El chequeo de guardado mira `imagenes.pristine`; patchValue no lo marca.
      this.imagenes.markAsDirty();
    } catch (err) {
      console.error(err);
      this.helper.notifError(err);
    }
    this.loading = false;
  }
}
